// Regras de telefone com suporte a número internacional.
//
// Convenção: telefone que começa com "+" já traz o DDI informado pelo usuário
// (ex.: "+351 912 345 678") e é usado como está, só sem os caracteres não
// numéricos. Sem "+", o número é tratado como brasileiro: com 10 ou 11 dígitos
// (DDD + número) recebe o DDI 55.
//
// GÊMEO no backend: app/Support/Telefone.php — mudou a regra aqui, mude lá.

export const DDI_BRASIL = '55'

// Config da empresa que libera o telefone internacional no cadastro de clientes
// (tabela configurations → permissão CONF_*).
export const CONF_TELEFONE_INTERNACIONAL = 'CONF_CLIENTEPERMITIRTELEFONEINTERNACIONAL'

// "+" seguido de 8 a 15 dígitos (E.164), com espaço, hífen, ponto e parênteses
// como separadores.
export const REGEX_TELEFONE_INTERNACIONAL = /^\+(?=(?:\D*\d){8,15}\D*$)\d[\d\s\-().]*$/

// Mesmo critério da regra `telefone` do vee-validate (formato brasileiro).
export const REGEX_TELEFONE_BRASIL = /^(\(?\d{2}\)?\s)?(\d{4,5}-\d{4})$/

export const ehTelefoneInternacional = (telefone) => String(telefone ?? '').trim().startsWith('+')

export const apenasDigitos = (telefone) => String(telefone ?? '').replace(/\D/g, '')

/**
 * Número pronto para wa.me / envio (só dígitos, com DDI).
 *
 * - Começa com "+": só os dígitos, sem acrescentar nada.
 * - 10 ou 11 dígitos (DDD + número brasileiro): acrescenta 55.
 * - Demais casos: devolve os dígitos. `prefixarDemais` preserva o legado de
 *   quem sempre acrescentava o 55: `true` acrescenta se ainda não começar com
 *   55; uma função `(digitos) => boolean` decide caso a caso.
 */
export const telefoneParaWhatsapp = (telefone, { prefixarDemais = false } = {}) => {
    const digitos = apenasDigitos(telefone)

    if (!digitos) {
        return ''
    }

    if (ehTelefoneInternacional(telefone)) {
        return digitos
    }

    if (digitos.length === 10 || digitos.length === 11) {
        return DDI_BRASIL + digitos
    }

    const devePrefixar = typeof prefixarDemais === 'function'
        ? prefixarDemais(digitos)
        : (prefixarDemais && !digitos.startsWith(DDI_BRASIL))

    return devePrefixar ? DDI_BRASIL + digitos : digitos
}

/** Só dígitos, mas mantendo o "+" inicial do número internacional. */
export const digitosPreservandoMais = (telefone) => {
    const digitos = apenasDigitos(telefone)
    return digitos && ehTelefoneInternacional(telefone) ? `+${digitos}` : digitos
}

/** Máscara brasileira progressiva: (11) 9876-5432 / (11) 98765-4321. */
export const formatarTelefoneBrasil = (valor) => {
    const d = apenasDigitos(valor).slice(0, 11)

    if (!d) return ''
    if (d.length <= 2) return `(${d}`

    const ddd = d.slice(0, 2)
    const resto = d.slice(2)

    if (resto.length <= 4) return `(${ddd}) ${resto}`
    if (d.length <= 10) return `(${ddd}) ${resto.slice(0, 4)}-${resto.slice(4)}`

    return `(${ddd}) ${resto.slice(0, 5)}-${resto.slice(5)}`
}

/**
 * Formatter do campo de telefone com a config de internacional ligada:
 * começando com "+", mantém o que foi digitado (dígitos e separadores, até 15
 * dígitos); sem "+", aplica a máscara brasileira de sempre.
 */
export const formatarTelefoneEntrada = (valor) => {
    const texto = String(valor ?? '')

    if (!ehTelefoneInternacional(texto)) {
        return formatarTelefoneBrasil(texto)
    }

    let digitosVistos = 0
    let saida = '+'
    for (const ch of texto.trim().slice(1)) {
        if (/\d/.test(ch)) {
            if (digitosVistos >= 15) continue
            digitosVistos++
            saida += ch
        } else if (/[\s\-().]/.test(ch) && digitosVistos > 0) {
            saida += ch
        }
    }

    return saida.replace(/\s{2,}/g, ' ')
}

/** Telefone válido no cadastro: brasileiro com máscara ou internacional com "+". */
export const telefoneValidoCadastro = (valor) => {
    const texto = String(valor ?? '').trim()
    return REGEX_TELEFONE_BRASIL.test(texto) || REGEX_TELEFONE_INTERNACIONAL.test(texto)
}
